import { useState } from 'react'
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { CopyDialog, DeleteConfirmDialog } from '@library'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { MOBILE_SEARCH_ASSISTANTS_COLUMNS } from '@mobileConstants'
import { exportToJSON } from '@mobileHelpers'
import { ConfigListPageShell } from '@mobileComponents/ConfigListPageShell'
import { useMobileSearchAssistants } from '@mobileHooks'
import { Add as AddIcon } from '@mui/icons-material'
import { Box, Button } from '@mui/material'

import { generateDataGridActionsColumn } from '@helpers'
import { ROUTES } from '@constants'

export const ConfigMobileSearchAssistants = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isAllSearchAssistants = matchPath(ROUTES.CONFIG_MOBILE_SEARCH_ASSISTANTS, pathname)

  const { searchAssistants, loading, copySearchAssistant, deleteSearchAssistant } =
    useMobileSearchAssistants()

  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [searchAssistantToCopy, setSearchAssistantToCopy] = useState<{
    id: string
    title: string
  } | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [searchAssistantToDelete, setSearchAssistantToDelete] = useState<{
    id: string
    title: string
  } | null>(null)

  const handleCreate = () => {
    navigate(ROUTES.CONFIG_MOBILE_SEARCH_ASSISTANTS_CREATE)
  }

  const handleEdit = (id: string) => {
    navigate(`${ROUTES.CONFIG_MOBILE_SEARCH_ASSISTANTS_EDIT}/${id}`)
  }

  const handleCopyClick = (id: string) => {
    const searchAssistant = searchAssistants.find(sa => sa.id === id)
    if (searchAssistant) {
      setSearchAssistantToCopy({ id: searchAssistant.id, title: searchAssistant.title })
      setCopyDialogOpen(true)
    }
  }

  const handleCopy = (newTitle: string) => {
    if (searchAssistantToCopy) {
      const newSearchAssistant = copySearchAssistant(searchAssistantToCopy.id, newTitle)
      if (newSearchAssistant) {
        setCopyDialogOpen(false)
        setSearchAssistantToCopy(null)
        navigate(`${ROUTES.CONFIG_MOBILE_SEARCH_ASSISTANTS_EDIT}/${newSearchAssistant.id}`)
      }
    }
  }

  const handleDeleteClick = (id: string) => {
    const searchAssistant = searchAssistants.find(sa => sa.id === id)
    if (searchAssistant) {
      setSearchAssistantToDelete({ id: searchAssistant.id, title: searchAssistant.title })
      setDeleteDialogOpen(true)
    }
  }

  const handleDelete = () => {
    if (searchAssistantToDelete) {
      deleteSearchAssistant(searchAssistantToDelete.id)
      setDeleteDialogOpen(false)
      setSearchAssistantToDelete(null)
    }
  }

  const handleExport = (id: string) => {
    const searchAssistant = searchAssistants.find(sa => sa.id === id)
    if (searchAssistant) {
      exportToJSON(searchAssistant, `${searchAssistant.title}_search_assistant_config.json`)
    }
  }

  const columns = [
    ...MOBILE_SEARCH_ASSISTANTS_COLUMNS,
    generateDataGridActionsColumn({
      onEdit: params => handleEdit(params.row.id),
      onCopy: params => handleCopyClick(params.row.id),
      onDelete: params => handleDeleteClick(params.row.id),
      onDowloadButtonClick: params => handleExport(params.row.id),
    }),
  ]

  return (
    <>
      <Outlet />

      <CopyDialog
        open={copyDialogOpen}
        title='Copy Mobile Search Assistant'
        initialName={searchAssistantToCopy?.title || ''}
        existingNames={searchAssistants.map(sa => sa.title)}
        onClose={() => {
          setCopyDialogOpen(false)
          setSearchAssistantToCopy(null)
        }}
        onCopy={handleCopy}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title='Delete Mobile Search Assistant'
        message='Are you sure you want to delete search assistant'
        itemName={searchAssistantToDelete?.title}
        onClose={() => {
          setDeleteDialogOpen(false)
          setSearchAssistantToDelete(null)
        }}
        onConfirm={handleDelete}
      />

      <ConfigListPageShell
        title='Mobile Search Assistants'
        isVisible={!!isAllSearchAssistants}
        actions={
          <Button variant='contained' startIcon={<AddIcon />} onClick={handleCreate}>
            Create
          </Button>
        }
      >
          <Box sx={{ display: 'flex', flex: 1 }}>
            <DataGrid
              rows={searchAssistants}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              hideFooter
            />
          </Box>
      </ConfigListPageShell>
    </>
  )
}
